import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Spin } from 'antd';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { checkAuth, logout } from './store/slices/authSlice';
import ErrorBoundary from './components/ErrorBoundary';
import Footer from './components/Footer';
import useMobileDetection from './hooks/useMobileDetection';
import { 
  UserOutlined, 
  LogoutOutlined, 
  DashboardOutlined,
  TeamOutlined,
  DatabaseOutlined,
  SoundOutlined,
  GlobalOutlined,
  DollarOutlined,
  MenuOutlined,
  CreditCardOutlined,
  ScanOutlined,
  BankOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserManagement from './components/UserManagement';
import UIDManagement from './components/UIDManagement';
import TextToVoice from './components/TextToVoice';
import ProxyManagement from './components/ProxyManagement';
import PricingManagement from './components/PricingManagement';
import PricingTable from './components/PricingTable';
import Profile from './components/Profile';
import RequestLogs from './components/RequestLogs';
import PaymentScannerPage from './components/PaymentScannerPage';
import PaymentHistory from './components/PaymentHistory';
import PaymentStatus from './components/PaymentStatus';
import AdminPaymentHistory from './components/AdminPaymentHistory';
import './App.css';

const { Header, Content, Sider } = Layout;

function AppContent() {
  const dispatch = useDispatch();
  const { user, isAuthenticated, isLoading } = useSelector(state => state.auth);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobileDetection();

  // Check authentication on mount
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  // Close mobile menu whenever switching back to desktop
  useEffect(() => {
    if (!isMobile && mobileMenuVisible) {
      setMobileMenuVisible(false);
    }
  }, [isMobile, mobileMenuVisible]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated || !user) {
    return <Login />;
  }

  const handleLogout = () => {
    dispatch(logout());
  };

  const userMenu = {
    items: [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: 'Profile',
        onClick: () => navigate('/profile')
      },
      {
        type: 'divider'
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: handleLogout
      }
    ]
  };

  // Role-based menu items
  const getMenuItems = () => {
    // Ensure user object exists
    if (!user) {
      return [];
    }

    const baseItems = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      },
    ];

    // User-only items (not for admin)
    const userItems = [
      {
        key: '/text-to-voice',
        icon: <SoundOutlined />,
        label: 'Text to Voice',
      },
      {
        key: '/pricing',
        icon: <DollarOutlined />,
        label: 'Pricing Plans',
      },
      {
        key: '/payment-history',
        icon: <CreditCardOutlined />,
        label: 'Payment History',
      },
      {
        key: '/payment-status',
        icon: <ScanOutlined />,
        label: 'Payment Status',
      },
      {
        key: '/profile',
        icon: <UserOutlined />,
        label: 'Profile',
      },
    ];

    // Admin-only items
    const adminItems = [
      {
        key: '/users',
        icon: <TeamOutlined />,
        label: 'Users',
      },
      {
        key: '/uids',
        icon: <DatabaseOutlined />,
        label: 'UIDs',
      },
      {
        key: '/proxies',
        icon: <GlobalOutlined />,
        label: 'Proxies',
      },
      {
        key: '/pricing-admin',
        icon: <DollarOutlined />,
        label: 'Pricing Plans',
      },
      {
        key: '/request-logs',
        icon: <DatabaseOutlined />,
        label: 'Request Logs',
      },
      {
        key: '/admin-payment-history',
        icon: <BarChartOutlined />,
        label: 'Payment History',
      },
    ];

    // Admin gets baseItems + adminItems
    // Regular users get baseItems + userItems (Profile)
    return user?.is_admin ? [...baseItems, ...adminItems] : [...baseItems, ...userItems];
  };

  const menuItems = getMenuItems() || [];
  const handleMenuClick = ({ key }) => {
    navigate(key);
    setMobileMenuVisible(false);
  };

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header className="app-header">
        <div className="header-left">
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
            className="mobile-menu-button"
            style={{ color: 'white', fontSize: '18px' }}
          />
          <div className="logo">
            Voice Synthesis
          </div>
        </div>
        <div className="user-info">
          <span className="welcome-text">Welcome, {user?.username || 'User'}</span>
          {user?.is_admin && <span className="admin-badge">ADMIN</span>}
            <Dropdown menu={userMenu} placement="bottomRight">
            <Button type="text" style={{ color: 'white' }}>
              <UserOutlined />
            </Button>
          </Dropdown>
        </div>
      </Header>
      
      <Layout style={{ flex: 1 }}>
        <Sider
          width={200}
          collapsed={collapsed}
          onCollapse={setCollapsed}
          className="desktop-sider"
          style={{
            background: '#fff',
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems || []}
            onClick={handleMenuClick}
            style={{ height: '100%', borderRight: 0 }}
          />
        </Sider>

        {/* Mobile Menu */}
        {mobileMenuVisible && (
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuVisible(false)}>
            <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
              <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                items={menuItems || []}
                onClick={handleMenuClick}
                style={{ border: 'none' }}
              />
            </div>
          </div>
        )}

        <Layout className="app-main-layout" style={{ flex: 1 }}>
          <Content className="app-content">
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              {!user?.is_admin && (
                <>
                  <Route path="/text-to-voice" element={<TextToVoice />} />
                  <Route path="/pricing" element={<PricingTable />} />
                  <Route path="/payment-scanner/:planId" element={<PaymentScannerPage />} />
                  <Route path="/payment-history" element={<PaymentHistory />} />
                  <Route path="/payment-status" element={<PaymentStatus />} />
                  <Route path="/profile" element={<Profile />} />
                </>
              )}
              {user?.is_admin && (
                <>
                  <Route path="/users" element={<UserManagement />} />
                  <Route path="/uids" element={<UIDManagement />} />
                  <Route path="/proxies" element={<ProxyManagement />} />
                  <Route path="/pricing-admin" element={<PricingManagement />} />
                  <Route path="/request-logs" element={<RequestLogs />} />
                  <Route path="/admin-payment-history" element={<AdminPaymentHistory />} />
                </>
              )}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
      
      <Footer />
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={<Spin size="large" />} persistor={persistor}>
          <Router>
            <AppContent />
          </Router>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;